/**
=========================================================
* Soft UI Dashboard PRO React - v4.0.0
=========================================================

* Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-pro-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import React, { useState, useRef, useEffect, useMemo } from "react";
import moment from "moment";
import { formatCompanyDateTime } from "../../../helpers/companyTime";
import { KPI_IMAGE_PARAMS, isCompositeKpi } from "../../../constants/kpi";
import { FETCH_JOB } from "../../../api/jobs";
import { FETCH_ESTIMATE } from "../../../api/estimates";
import { FETCH_REVIEW } from "../../../api/reviews";
import { GET_IMAGE } from "../../../helpers/images";
import KpiReferenceImage from "../KpiReferenceImage";
import { UseIsAdmin } from "../../../hooks/auth";
import { formatNumber } from "../../../helpers/formatter";
import {
  buildBidAccuracyDetailRows,
  buildDetailRows,
  formatIntegratedRefSubtitle,
  formatReviewRatingBadge,
  getHcpTagColor,
  getMembershipPlanName,
  getTimelineJobId,
  getTimelineRefId,
  getTimelineRefModel,
  isBidAccuracyCalcEntry,
  resolveJobSubtitlesFromJob,
  resolveJobTag,
} from "../../../helpers/timelineEntry";

// prop-types is a library for typechecking of props
import PropTypes from "prop-types";

// @mui material components
import {
  useTheme,
  Backdrop,
  Dialog,
  Grid,
  IconButton,
  Modal,
  Fade,
} from "@mui/material";
import { createTheme, ThemeProvider } from "@mui/material/styles";

// Soft UI Dashboard PRO React components
import SoftBox from "../../SoftBox";
import SoftInput from "../../SoftInput";
import SoftTypography from "../../SoftTypography";
import SoftDialog from "../../SoftConfirmationDialog";
import ResponsiveModal from "../../ResponsiveModal";
import SoftButton from "../../SoftButton";
import FeedItem from "../../../pages/Feed/FeedItem";
// import ConfirmatonModal from "../../Modals/ConfirmationModal";
// import InputModal from "../../Modals/InputModal";
import useMediaQuery from "@mui/material/useMediaQuery";

import CloseIcon from '@mui/icons-material/Close';
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import StarRoundedIcon from "@mui/icons-material/StarRounded";
import StarBorderRoundedIcon from "@mui/icons-material/StarBorderRounded";

// Timeline context
import { useTimeline } from "../context";

// Icon
import { ReactComponent as MessageIcon } from "../../../img/timeline-messageicon.svg";
import ArrowForwardIosRoundedIcon from "@mui/icons-material/ArrowForwardIosRounded";
import { Edit as EditIcon, DeleteOutline } from "@mui/icons-material";
import toast from "react-hot-toast";
import { Spinner } from "../../shared/Loading";

function TimelineItem({
  color = "info",
  icon,
  title,
  date,
  description = "",
  lastItem = false,
  time,
  eKpidId,
  notesText,
  timeline,
  handleOpenNotes,
  handleKpiUpdate,
  handleKpiDelete,
  deletedAt,
  variant = "default",
  subtitle1 = null,
  technicianName = null,
  jobTag = null,
  refModel = null,
  refId = null,
  refBadge = null,
  formFields = [],
  descriptionParts = null,
  hideAddedBy = false,
}) {
  const isDark = useTimeline();
  const theme = useTheme();
  const {
    primary: { main: primaryColor },
  } = theme.palette;
  // FeedItem lays itself out from the real window width (sx breakpoints,
  // Grid columns, useMediaQuery), which is correct on its native full-width
  // Feed page but wrong here: the "View Job" Dialog is a fixed ~600px box
  // on every device. Pushing every breakpoint above "sm" out of reach makes
  // FeedItem always render its compact/stacked layout inside this dialog,
  // matching what it already renders correctly on an actual phone.
  const jobModalTheme = useMemo(
    () =>
      createTheme(theme, {
        breakpoints: {
          values: {
            ...theme.breakpoints.values,
            md: 99999,
            lg: 99999,
            xl: 99999,
            xxl: 99999,
            xxxl: 99999,
            wide: 99999,
            wider: 99999,
          },
        },
      }),
    [theme],
  );
  const isAdmin = UseIsAdmin();
  const [openNotes, setOpenNotes] = useState(false);
  const [messages, setMessages] = useState(null);
  const [referenceItem, setReferenceItem] = useState(null);
  const [isLoadingJob, setIsLoadingJob] = useState(false);
  const savedImageRotationsRef = useRef({});
  const [viewDetails, setViewDetails] = useState(false);
  const isSmallScreen = useMediaQuery((themes) => themes.breakpoints.down("md"));
  // The row's parent Grid item only shrinks to 8/12 width at the "xxl"
  // breakpoint (see Timeline.jsx's Grid item mini={12} xxl={8}) — the 150%
  // row width below exists solely to compensate for that shrink so the row
  // still spans the full card. Below "xxl" the Grid item is already 100%
  // wide, so stretching the row to 150% there just pushes the comment/edit/
  // delete buttons off the right edge of the visible card (needing a
  // horizontal scroll most people never find, at every normal desktop
  // width — not just small/mobile screens).
  const isXxlUp = useMediaQuery((themes) => themes.breakpoints.up("xxl"));
  const [openModal, setOpenModal] = useState(false);
  const formRef = useRef(null);
  const handleCloseModal = () => {
    setOpenModal(false);
  };
  const [newScore, setNewScore] = useState(null);
  const isCompositeEntry =
    timeline?.metadata?.isComposite === true || isCompositeKpi(timeline?._kpiDef);
  // Composite is calculated from source metrics — don't allow manual edit/delete.
  const hideScoreEditActions = isCompositeEntry;
  const handleSubmit = () => {
    if (hideScoreEditActions) {
      handleCloseModal();
      return;
    }
    handleCloseModal();
    handleKpiUpdate(timeline._id, timeline.kpiId, newScore, timeline.amount, timeline.points, timeline.ownerId, timeline.currentValue);
  };

  const [openConfirmationPopUp, setOpenConfirmationPopUp] = useState(false);
  const [jobPreview, setJobPreview] = useState(null);
  const [refPreview, setRefPreview] = useState(null);
  const [viewRefDetails, setViewRefDetails] = useState(false);
  const [estimatePreview, setEstimatePreview] = useState(null);
  const [viewEstimateDetails, setViewEstimateDetails] = useState(false);
  const isIntegratedLayout = variant === "integrated-job" || variant === "integrated-ref";
  const resolvedRefModel = refModel || getTimelineRefModel(timeline);
  const resolvedRefId = refId || getTimelineRefId(timeline);
  const isEstimateEntry = resolvedRefModel === "Estimate";

  const timelineJobId = getTimelineJobId(timeline);
  useEffect(() => {
    if (variant !== "integrated-job") return undefined;
    if (!timelineJobId) return undefined;

    let cancelled = false;
    FETCH_JOB(timelineJobId)
      .then(({ data }) => {
        if (!cancelled) setJobPreview(data);
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
    // Depend on the job id string only — parent Timeline re-enriches entries
    // into new object refs often, which would re-fetch forever if `timeline`
    // were listed here.
  }, [variant, timelineJobId?._id, timeline?.refId, timeline?.metadata?.refId, timeline?.metadata?.jobId]);

  useEffect(() => {
    if (variant !== "integrated-ref" || resolvedRefModel !== "Review" || !resolvedRefId) {
      return undefined;
    }

    let cancelled = false;
    FETCH_REVIEW(resolvedRefId)
      .then(({ data }) => {
        if (!cancelled) setRefPreview(data);
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, [variant, resolvedRefModel, resolvedRefId]);

  useEffect(() => {
    if (variant !== "integrated-ref" || !isEstimateEntry || !resolvedRefId) {
      return undefined;
    }

    let cancelled = false;
    FETCH_ESTIMATE(resolvedRefId)
      .then(({ data }) => {
        if (!cancelled) setEstimatePreview(data);
      })
      .catch(() => {
        if (!cancelled) setEstimatePreview(null);
      });

    return () => {
      cancelled = true;
    };
  }, [variant, isEstimateEntry, resolvedRefId]);

  const resolvedJobTag = jobTag || resolveJobTag(timeline, jobPreview);
  const jobSubtitles = resolveJobSubtitlesFromJob(jobPreview);
  const refSubtitle = formatIntegratedRefSubtitle(timeline.metadata, refPreview);
  const estimateSubtitle = isEstimateEntry
    ? estimatePreview?.metadata?.customerName || null
    : null;
  // Up Sell scores carry their own HCP line-item description (or blank). Never
  // fall through to the job-level subtitle — that shows the wrong service name.
  const displaySubtitle1 = timeline?.metadata?.isUpsell
    ? subtitle1 || null
    : subtitle1 || jobSubtitles.subtitle1 || estimateSubtitle || refSubtitle;
  const displayTechnician = technicianName || jobSubtitles.technicianName;
  const displayRefBadge =
    refBadge || (resolvedRefModel === "Review" ? formatReviewRatingBadge(refPreview?.rating) : null);
  const isDeletedEntry = title === "Deleted Score";
  const isClaimActivityEntry =
    timeline?.scoreType === "CLAIMED" || timeline?.scoreType === "UNCLAIMED";
  const displayTitle = isDeletedEntry ? `Deleted ${timeline.kpiName} Score` : title;

  const handleConfirmationClose = () => setOpenConfirmationPopUp(false);

  const handleConfirmationOpen = () => {
    handleConfirmationClose();
    setOpenConfirmationPopUp(true);
  };

  const handleOpen = () => {
    setOpenNotes(true);
    handleOpenNotes?.(eKpidId);
  };

  const handleClose = () => {
    setOpenNotes(false);
  };

  const formattedAmt = formatNumber(timeline.amount, timeline.kind);

  const showItem = ({ jobId, imageKey, notes, entryId, imageRotation, orientationSaved }) => {
    if (jobId) {
      // Previously silent on both slow and failed fetches — the button just
      // sat there with no feedback, which reads as stuck rather than loading.
      setIsLoadingJob(true);
      FETCH_JOB(jobId)
        .then(({ data }) => {
          // API returns 200 with null when the Job doc is missing. Guard so we
          // don't open an empty dialog that looks like a no-op click.
          if (!data) {
            setReferenceItem({ jobMissing: true });
            return;
          }
          setReferenceItem({ job: data });
        })
        .catch(() => {
          toast.error("Couldn't load this job. Please try again.");
        })
        .finally(() => setIsLoadingJob(false));
    } else if (imageKey) {
      const resolvedRotation =
        (entryId && savedImageRotationsRef.current[entryId]) ?? imageRotation ?? 0;
      const resolvedOrientationSaved =
        (entryId && savedImageRotationsRef.current[entryId] !== undefined) ||
        orientationSaved;
      setReferenceItem({
        imageKey,
        notes,
        entryId,
        imageRotation: resolvedRotation,
        orientationSaved: resolvedOrientationSaved,
      });
    }
  };

  // Solid paper to match the Feed Settings modal — the frosted-glass look
  // reads as washed-out over timeline content showing through behind it.
  const styleModal = {
    position: 'fixed',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    background: '#FFFFFF',
    borderRadius: '24px',
    boxShadow: '0px 24px 48px -8px rgba(16,24,40,0.18), inset 0px 1px 0px 0px rgba(255,255,255,0.7)',
    padding: '1.75rem',
    maxWidth: { mini: "90%", xs: "90%", sm: "600px" },
    width: "100%",
    overflow: "visible",
    border: '1px solid rgba(15,23,42,0.08)',
  };

  const jobId = getTimelineJobId(timeline);
  const reviewDetailsText = refPreview?.content || timeline.metadata?.content || "None";
  const reviewName = refPreview?.name || timeline.metadata?.name || "Anonymous reviewer";
  const rawReviewRating = Number(refPreview?.rating ?? timeline.metadata?.rating);
  const reviewRating = Number.isFinite(rawReviewRating)
    ? Math.min(5, Math.max(0, rawReviewRating))
    : null;

  let refItem = null;
  if (jobId) {
    // Show Job Feed item
    refItem = (
      <SoftButton
        variant="text"
        size="small"
        color="primary"
        onClick={() => showItem({ jobId })}
        disabled={isLoadingJob}
        sx={{ paddingX: 0 }}
      >
        {isLoadingJob ? (
          <Spinner size={16} color="primary" label="Loading job…" />
        ) : (
          <>
            View Job{" "}
            <ArrowForwardIosRoundedIcon
              sx={{ marginLeft: "0.25rem", color: "primary.main" }}
            />
          </>
        )}
      </SoftButton>
    );
  } else if (variant === "integrated-ref" && resolvedRefModel === "Review" && resolvedRefId) {
    refItem = (
      <>
        <SoftButton
          variant="text"
          size="small"
          color="primary"
          sx={{ paddingX: 0, display: isDeletedEntry ? "none" : "" }}
          onClick={() => setViewRefDetails(true)}
        >
          View Review
          <ArrowForwardIosRoundedIcon
            sx={{ marginLeft: "0.25rem", color: "primary.main" }}
          />
        </SoftButton>
        <ResponsiveModal
          open={viewRefDetails}
          onClose={() => setViewRefDetails(false)}
          title="Review"
          maxWidth="xs"
        >
          <SoftBox
            sx={{
              padding: "12px",
              marginBottom: "16px",
              border: "1px solid #EAECF0",
              borderRadius: "10px",
              backgroundColor: "#F9FAFB",
            }}
          >
            <SoftTypography
              sx={{
                color: "#101828",
                fontFamily: "DM Sans",
                fontSize: "15px!important",
                fontWeight: "500!important",
                lineHeight: 1.4,
              }}
            >
              {reviewName}
            </SoftTypography>
            {reviewRating !== null && (
              <SoftBox
                aria-label={`${reviewRating} out of 5 stars`}
                sx={{ display: "flex", alignItems: "center", gap: "2px", marginTop: "6px" }}
              >
                {Array.from({ length: 5 }, (_, index) => {
                  const RatingIcon = index < Math.round(reviewRating)
                    ? StarRoundedIcon
                    : StarBorderRoundedIcon;
                  return (
                    <RatingIcon
                      key={`review-star-${index}`}
                      aria-hidden="true"
                      sx={{ color: "#F59E0B", fontSize: "20px" }}
                    />
                  );
                })}
                <SoftTypography
                  component="span"
                  sx={{ color: "#667085", fontSize: "12px", fontWeight: 500, marginLeft: "4px" }}
                >
                  {reviewRating}/5
                </SoftTypography>
              </SoftBox>
            )}
          </SoftBox>

          <SoftTypography
            component="p"
            sx={{
              margin: 0,
              color: "#344054",
              fontFamily: "DM Sans",
              fontSize: { mini: "14px", sm: "15px" },
              fontWeight: 400,
              lineHeight: 1.6,
              whiteSpace: "pre-wrap",
              overflowWrap: "anywhere",
            }}
          >
            {reviewDetailsText}
          </SoftTypography>
        </ResponsiveModal>
      </>
    );
  } else if (variant === "integrated-ref" && isEstimateEntry && resolvedRefId) {
    const estMeta = estimatePreview?.metadata || {};
    const tlMeta = timeline.metadata || {};
    const membershipPlan = getMembershipPlanName(tlMeta);
    // Commission job rows keep customer/invoice on the score metadata; the
    // linked Estimate doc may be missing or sparse, so fall back to tlMeta.
    const detailCustomer =
      estMeta.customerName || tlMeta.customer_name || tlMeta.customerName || null;
    const detailInvoice =
      estMeta.invoiceNumber || tlMeta.jobInvoiceNumber || tlMeta.invoiceNumber || null;
    const detailStatus = estMeta.status || null;
    const detailLocation = estMeta.location || null;

    const summaryRows = [];
    if (membershipPlan) {
      // Membership entries reference their originating estimate but score a
      // plan signup — show the plan/invoice, not the Won-value math (their
      // amount is a unit count, not dollars).
      summaryRows.push({ label: "Plan", value: membershipPlan, strong: true });
      if (detailCustomer) {
        summaryRows.push({ label: "Customer", value: String(detailCustomer) });
      }
      if (detailInvoice) {
        summaryRows.push({ label: "Invoice #", value: String(detailInvoice) });
      }
      const membershipScheduled =
        estimatePreview?.extRefDate
        || tlMeta.jobScheduledAt
        || tlMeta.scheduledStart
        || tlMeta.scheduledAt
        || null;
      if (membershipScheduled) {
        const scheduled = new Date(membershipScheduled);
        if (!Number.isNaN(scheduled.getTime())) {
          summaryRows.push({
            label: "Scheduled",
            value: formatCompanyDateTime(scheduled),
          });
        }
      }
    } else {
      // Estimate Won Value rows store estimateLabor / estimateDiscount*.
      // Job-completed Commission rows store jobLabor / jobDiscount* instead —
      // without this branch the popover only printed "Won value" from the
      // scored amount (which is 1% after share), so it looked incomplete.
      const labor = tlMeta.estimateLabor ?? tlMeta.jobLabor;
      const discountPct = tlMeta.estimateDiscountPct ?? tlMeta.jobDiscountPct;
      const discountFixed = tlMeta.estimateDiscountFixed ?? tlMeta.jobDiscountFixed;
      const isJobCommission = tlMeta.jobLabor != null || tlMeta.jobCompletedAt != null;
      // Prefer the stamp on the score row; fall back to the KPI's configured share
      // so older rows still show "Rate: 1%" in this modal.
      let rawShare = null;
      if (Number(tlMeta.share) > 0) rawShare = Number(tlMeta.share);
      else if (Number(timeline._kpiDef?.parameters?.share) > 0) {
        rawShare = Number(timeline._kpiDef.parameters.share);
      }
      const share = rawShare && rawShare < 1 ? rawShare : null;

      // Identity first — invoice / scheduled / completion. Customer is the
      // dialog title (detailCustomer), not repeated here.
      if (detailInvoice) {
        summaryRows.push({ label: "Invoice #", value: String(detailInvoice) });
      }
      const scheduledRaw =
        estimatePreview?.extRefDate
        || tlMeta.jobScheduledAt
        || tlMeta.scheduledStart
        || tlMeta.scheduledAt
        || tlMeta.scheduledDate
        || null;
      if (scheduledRaw) {
        const scheduled = new Date(scheduledRaw);
        if (!Number.isNaN(scheduled.getTime())) {
          summaryRows.push({
            label: "Scheduled",
            value: formatCompanyDateTime(scheduled),
          });
        }
      }
      if (tlMeta.jobCompletedAt) {
        const done = new Date(tlMeta.jobCompletedAt);
        if (!Number.isNaN(done.getTime())) {
          summaryRows.push({
            label: "Job completed",
            value: formatCompanyDateTime(done),
          });
        }
      }

      if (tlMeta.estimateTotal != null) {
        summaryRows.push({ label: "Estimate total", value: formatNumber(Number(tlMeta.estimateTotal), "$") });
      }
      // Only when the sweep stored the breakdown — printing $0 for an entry
      // that simply predates the metadata reads as a wrong number, not a gap.
      if (labor != null) {
        summaryRows.push({
          label: "Line Items Total",
          value: formatNumber(Number(labor), "$"),
        });
      }
      if (Number(discountPct) > 0) {
        summaryRows.push({ label: "Discount", value: `${Number(discountPct)}%` });
      }
      if (Number(discountFixed) > 0) {
        summaryRows.push({
          label: "Fixed discount",
          value: formatNumber(Number(discountFixed), "$"),
        });
      }
      // "Won value" is EWV wording. A Self Generated entry credits a (possibly
      // unwon) estimate — its dollar rows read "Credited value", and the count
      // basis (always 1 per estimate) shows no amount row at all.
      // For Commission-with-share, "Won value" is the pre-share job base (labor
      // − discounts); the scored amount is shown separately as Commission.
      const isSelfGen = !!tlMeta.selfGeneratedTags;
      const isDollar = timeline.kind === "$";
      if (!isSelfGen || isDollar) {
        let wonAmount = Number(timeline.amount);
        if (isJobCommission && share) {
          const from = Number(tlMeta.shareBackfilledFrom);
          if (Number.isFinite(from) && from > 0) {
            wonAmount = from;
          } else if (labor != null) {
            const laborN = Number(labor) || 0;
            const pctOff = laborN * ((Number(discountPct) || 0) / 100);
            wonAmount = Math.max(0, laborN - pctOff - (Number(discountFixed) || 0));
          }
        }
        summaryRows.push({
          label: isSelfGen ? "Credited value" : "Won value",
          value: formatNumber(wonAmount, timeline.kind),
          strong: !share,
        });
        if (isJobCommission && share) {
          summaryRows.push({
            label: "Rate",
            value: `${Number((share * 100).toFixed(2))}%`,
          });
          summaryRows.push({
            label: "Commission",
            value: formatNumber(Number(timeline.amount), timeline.kind),
            strong: true,
          });
        }
      }
    }

    const labelStyle = { color: "#98A2B3", fontWeight: 400 };
    const valueStyle = { color: "#CED4DA", fontWeight: 600, textAlign: "right" };

    refItem = (
      <>
        <SoftButton
          variant="text"
          size="small"
          color="primary"
          sx={{ paddingX: 0, display: isDeletedEntry ? "none" : "" }}
          onClick={() => setViewEstimateDetails(true)}
        >
          View Estimate
          <ArrowForwardIosRoundedIcon
            sx={{ marginLeft: "0.25rem", color: "primary.main" }}
          />
        </SoftButton>
        <Dialog
          open={viewEstimateDetails}
          onClose={() => setViewEstimateDetails(false)}
          sx={{
            ".MuiPaper-root": {
              borderRadius: "10px",
              width: "100%",
              background:
                "linear-gradient(156deg, rgba(12, 12, 13, 0.50) 3.85%, rgba(12, 12, 13, 0.65) 50.21%, rgba(12, 12, 13, 0.51) 98.53%)",
              boxShadow:
                "-15.85294px -15.85294px 26.42157px 0px rgba(0, 0, 0, 0.20) inset, 15.85294px 15.85294px 31.70588px 0px rgba(0, 0, 0, 0.20)",
              backdropFilter: "blur(20px)",
              padding: { mini: "1rem", md: "1.5rem 1.5rem" },
            },
          }}
        >
          <SoftBox display="flex" flexDirection="column" gap={1.25}>
            {detailCustomer ? (
              <SoftTypography
                variant="h6"
                sx={{ color: "#FFFFFF", fontWeight: 600 }}
              >
                {detailCustomer}
              </SoftTypography>
            ) : null}
            {(detailStatus || detailLocation) ? (
              <SoftBox display="flex" flexDirection="column" gap={0.25}>
                {detailStatus ? (
                  <SoftTypography
                    variant="button"
                    sx={{ color: "#CED4DA", fontWeight: 400, textTransform: "capitalize" }}
                  >
                    {String(detailStatus).replaceAll("_", " ")}
                  </SoftTypography>
                ) : null}
                {detailLocation ? (
                  <SoftTypography
                    variant="button"
                    sx={{ color: "#98A2B3", fontWeight: 400 }}
                  >
                    {detailLocation}
                  </SoftTypography>
                ) : null}
              </SoftBox>
            ) : null}

            <SoftBox
              display="flex"
              flexDirection="column"
              gap={0.5}
              mt={0.5}
              pt={1}
              sx={{ borderTop: "1px solid rgba(255, 255, 255, 0.10)" }}
            >
              {summaryRows.map((row) => (
                <SoftBox
                  key={row.label}
                  display="flex"
                  justifyContent="space-between"
                  alignItems="baseline"
                  gap={2}
                >
                  <SoftTypography
                    variant="button"
                    sx={{ ...labelStyle, ...(row.strong && { color: "#CED4DA", fontWeight: 600 }) }}
                  >
                    {row.label}
                  </SoftTypography>
                  <SoftTypography variant="button" sx={valueStyle}>
                    {row.value}
                  </SoftTypography>
                </SoftBox>
              ))}
            </SoftBox>
            {estMeta.hcpWebId ? (
              <SoftTypography
                component="a"
                href={`https://pro.housecallpro.com/app/estimates/${estMeta.hcpWebId}`}
                target="_blank"
                rel="noopener noreferrer"
                variant="button"
                sx={{
                  color: "#B692F6",
                  fontWeight: 600,
                  marginTop: "0.25rem",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "4px",
                  "&:hover": { color: "#D6BBFB" },
                }}
              >
                Open in Housecall Pro
                <ArrowForwardIosRoundedIcon sx={{ fontSize: "12px !important" }} />
              </SoftTypography>
            ) : null}
          </SoftBox>
        </Dialog>
      </>
    );
  } else if (isBidAccuracyCalcEntry(timeline)) {
    // Bid Accuracy monthly calc row: show the computation breakdown the
    // server stored on the entry (completed jobs, forms, average).
    const bidRows = buildBidAccuracyDetailRows(timeline);

    refItem = (
      <>
        <SoftButton
          variant="text"
          size="small"
          color="primary"
          sx={{ paddingX: 0, display: isDeletedEntry ? "none" : "" }}
          onClick={() => setViewDetails(true)}
        >
          View Details
          <ArrowForwardIosRoundedIcon
            sx={{ marginLeft: "0.25rem", color: "primary.main" }}
          />
        </SoftButton>
        <ResponsiveModal
          open={viewDetails}
          onClose={() => setViewDetails(false)}
          title="Details"
          maxWidth="xs"
        >
          <SoftBox display="flex" flexDirection="column" gap="8px">
            {bidRows.map((row) => (
              <SoftBox
                key={row.key}
                display="flex"
                justifyContent="space-between"
                alignItems="baseline"
                gap={2}
                sx={{
                  padding: "10px 12px",
                  border: "1px solid #EAECF0",
                  borderRadius: "10px",
                  backgroundColor: "#F9FAFB",
                }}
              >
                <SoftTypography
                  sx={{
                    color: "#667085",
                    fontSize: "14px",
                    fontWeight: 400,
                    ...(row.strong && { color: "#344054", fontWeight: 600 }),
                  }}
                >
                  {row.label}
                </SoftTypography>
                <SoftTypography
                  sx={{ color: "#101828", fontSize: "14px", fontWeight: 600, textAlign: "right" }}
                >
                  {row.value}
                </SoftTypography>
              </SoftBox>
            ))}
          </SoftBox>
        </ResponsiveModal>
      </>
    );
  } else if (!isIntegratedLayout || getMembershipPlanName(timeline.metadata)) {
    // Manual/default entries, plus integrated Membership entries whose
    // originating estimate couldn't be linked (no refId) — those still need a
    // View Details that shows the plan / customer / invoice.
    // Find if an image item exists
    const imageItem =
      timeline.metadata &&
      Object.keys(timeline.metadata).find((item) =>
        KPI_IMAGE_PARAMS.some((key) => item.includes(key)),
      );
    // Compile input parameters, but skip the image item
    const notes =
      timeline.metadata &&
      Object.values(timeline.metadata)
        .filter((item) => item !== timeline.metadata[imageItem] && typeof item !== 'object')
        .join("\n");

    const detailRows = buildDetailRows(timeline.metadata);

    

    if (imageItem) {
      refItem = (
        <SoftButton
          variant="text"
          size="small"
          color="primary"
          onClick={() =>
            showItem({
              imageKey: timeline.metadata[imageItem],
              notes,
              entryId: timeline._id,
              imageRotation:
                savedImageRotationsRef.current[timeline._id] ??
                timeline.metadata?.imageRotation ??
                0,
              orientationSaved:
                savedImageRotationsRef.current[timeline._id] !== undefined ||
                (timeline.metadata && "imageRotation" in timeline.metadata),
            })
          }
          sx={{ paddingX: 0 }}
        >
          View Image{" "}
          <ArrowForwardIosRoundedIcon
            sx={{ marginLeft: "0.25rem", color: "primary.main" }}
          />
        </SoftButton>
      );
    } else {
      refItem = (
        <>
          <SoftButton
            variant="text"
            size="small"
            color="primary"
            sx={{ paddingX: 0, display: isDeletedEntry ? "none" : "" }}
            onClick={() => setViewDetails(true)}
          >
            View Details
            <ArrowForwardIosRoundedIcon
              sx={{ marginLeft: "0.25rem", color: "primary.main" }}
            />
            {/* {notes ?? "None"} */}
          </SoftButton>
          <ResponsiveModal
            open={viewDetails}
            onClose={() => setViewDetails(false)}
            title="Details"
            maxWidth="xs"
          >
            <SoftBox
              sx={{
                ".urlLink a": { color: "primary.main", textDecoration: "underline" },
                ".urlLink a:hover": { opacity: 0.75 },
              }}
            >
              {detailRows.length > 0 ? (
                <SoftBox display="flex" flexDirection="column" gap="8px">
                  {detailRows.map((row) => (
                    <SoftBox
                      key={row.key}
                      display="flex"
                      justifyContent={row.label ? "space-between" : "flex-start"}
                      alignItems="baseline"
                      gap={2}
                      sx={{
                        padding: "10px 12px",
                        border: "1px solid #EAECF0",
                        borderRadius: "10px",
                        backgroundColor: "#F9FAFB",
                      }}
                    >
                      {row.label ? (
                        <SoftTypography
                          sx={{ color: "#667085", fontSize: "14px", fontWeight: 400 }}
                        >
                          {row.label}
                        </SoftTypography>
                      ) : null}
                      {row.isUrl ? (
                        <SoftBox className="urlLink">
                          <a href={row.value} target="blank">
                            {row.value}
                          </a>
                        </SoftBox>
                      ) : (
                        <SoftTypography
                          sx={{ color: "#101828", fontSize: "14px", fontWeight: 600, textAlign: "right" }}
                        >
                          {row.value}
                        </SoftTypography>
                      )}
                    </SoftBox>
                  ))}
                </SoftBox>
              ) : (
                <SoftTypography
                  component="p"
                  sx={{ margin: 0, color: "#667085", fontSize: "14px", fontWeight: 400, lineHeight: 1.6 }}
                >
                  None
                </SoftTypography>
              )}
            </SoftBox>
          </ResponsiveModal>
        </>
      );
    }
  }

  const renderDescription = () => {
    if (descriptionParts) {
      return (
        <SoftTypography
          variant="button"
          fontWeight="regular"
          sx={{ color: "rgba(108, 117, 125, 1)" }}
        >
          {descriptionParts.prefix}
          <strong>{descriptionParts.amount}</strong>
          {" "}
          <strong>{descriptionParts.kpiName}</strong>
          {descriptionParts.actionText}
          <strong>{descriptionParts.playerName}</strong>
          {descriptionParts.suffix}
        </SoftTypography>
      );
    }

    if (!description) return null;

    return (
      <SoftTypography
        variant="button"
        fontWeight="regular"
        sx={{
          color: "rgba(108, 117, 125, 1)",
          fontSize: isDeletedEntry ? "12px" : "",
          whiteSpace: "pre-line",
        }}
      >
        {isDeletedEntry ? description.replace("added to", "was deleted for") : description}
        {deletedAt ? ` on ${moment(deletedAt).format("MMM DD")}` : ""}
      </SoftTypography>
    );
  };

  return (
    <SoftBox display="grid" sx={{ gridTemplateColumns: "max-content 3fr", borderBottom: "1px solid #F2F4F7", marginBottom: "1rem", width: isXxlUp ? "150%" : "100%", minWidth: "100%" }}>
      <SoftBox display="flex" flexDirection="column" width="60px">
        <SoftTypography
          variant="button"
          fontWeight="medium"
          sx={{ color: "rgba(52, 58, 64, 1)" }}
        >
          {date}
        </SoftTypography>
        <SoftTypography
          variant="caption"
          sx={{ color: "rgba(52, 58, 64, 1)", fontWeight: 400 }}
        >
          {time}
        </SoftTypography>
      </SoftBox>
      <SoftBox position="relative">
        <SoftBox
          bgColor="transparent"
          width="1.625rem"
          height="1.625rem"
          borderRadius="50%"
          position="absolute"
          top="3.25%"
          left="2px"
          zIndex={2}
          display="flex"
          justifyContent="center"
          alignItems="center"
        >
          {icon}
        </SoftBox>
        <SoftBox ml={5.75} lineHeight={0}>
          <Grid container spacing={2}>
            <Grid item mini={9} xs={9} sm={9} xxl={9}>
              <SoftTypography variant="h6" fontWeight="medium" color="black" sx={{ fontSize: isSmallScreen || isDeletedEntry ? "16px" : "20px" }}>
                {displayTitle}
              </SoftTypography>
            </Grid>
            <Grid item mini={3} xs={3} sm={3} xxl={3} sx={{ display: "flex", justifyContent: "flex-end" }}>
              {((timeline.metricType !== 'anchor' || timeline.currentValue) && !isDeletedEntry && !isClaimActivityEntry) && (
                <SoftBox mt={0.5} mb={1.5}>
                  <SoftBox className="flex flex-row gap-1">
                    <SoftButton
                      variant="outlined"
                      iconOnly={true}
                      color="primary"
                      onClick={handleOpen}
                      sx={{
                        fontSize: "1rem",
                        width: "max-content",
                        height: "max-content",
                        minWidth: "max-content",
                        minHeight: "max-content",
                        padding: "0.18rem 0.18rem",
                        ...(notesText >= 1 && { padding: "0.05rem 0.35rem" }),
                      }}
                    >
                      <MessageIcon /> {notesText >= 1 ? notesText : ""}
                    </SoftButton>
                    {!hideScoreEditActions && (
                      <SoftButton
                        variant="outlined"
                        iconOnly={true}
                        color="primary"
                        onClick={() => setOpenModal(true)}
                        sx={{
                          fontSize: "1rem",
                          width: "max-content",
                          height: "max-content",
                          minWidth: "max-content",
                          minHeight: "max-content",
                          padding: "0.25rem",
                          display: isSmallScreen ? "none" : "",
                        }}
                      >
                        <EditIcon />
                      </SoftButton>
                    )}
                    {!hideScoreEditActions && (
                      <SoftButton
                        variant="outlined"
                        iconOnly={true}
                        color="primary"
                        onClick={handleConfirmationOpen}
                        disabled={timeline.currentValue}
                        sx={{
                          fontSize: "1rem",
                          width: "max-content",
                          height: "max-content",
                          minWidth: "max-content",
                          minHeight: "max-content",
                          padding: "0.25rem",
                          display: isSmallScreen || timeline.currentValue ? "none" : "",
                        }}
                      >
                        <DeleteOutline />
                      </SoftButton>
                    )}
                  </SoftBox>
                </SoftBox>
              )}
            </Grid>
          </Grid>
          <Modal
            open={openModal}
            onClose={handleCloseModal}
            BackdropComponent={Backdrop}
            BackdropProps={{
              timeout: 500,
            }}
          >
            <Fade in={openModal}>
              <SoftBox sx={styleModal}>
                <SoftBox
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <SoftTypography sx={{textFillColor: "#0F172A"}} variant="h6" component="h2">
                    Update Timeline Score
                  </SoftTypography>
                  <SoftButton
                    variant="outlined"
                    color="dark"
                    onClick={handleCloseModal}
                    sx={{
                      "& svg": { fontSize: "1.25rem !important" },
                      borderRadius: "50%",
                      minWidth: "max-content",
                      minHeight: "max-content",
                      padding: "0.25rem",
                      color: "#0F172A !important",
                      borderColor: "rgba(15,23,42,0.10) !important",
                    }}
                  >
                    <CloseIcon />
                  </SoftButton>
                </SoftBox>
                <hr style={{marginTop: '1rem', marginBottom: '1rem'}}/>
                {/* <InputModal
                  title={`Update ${timeline.kpiName} Score`}
                  question={`Do you want to update ${timeline.name}'s ${timeline.kpiName} KPI record? Current amount: ${formattedAmt}`}
                  yesText="Yes, update"
                  noText="No, cancel"
                  label="New Value"
                  isNumericInput={true}
                  onsuccess={(value) =>
                    handleKpiUpdate(timeline._id, timeline.kpiId, value, timeline.amount, timeline.points, timeline.ownerId, timeline.currentValue)
                  }
                > */}
                <SoftTypography sx={{textFillColor: "#475467", textAlign: 'center', fontSize: '14px', fontWeight: '400'}} variant="p" component="p">
                  Do you want to update {timeline.name}'s {timeline.kpiName} KPI record?
                </SoftTypography>
                <SoftTypography sx={{textFillColor: "#475467", textAlign: 'center', fontSize: '14px', fontWeight: '400', marginTop: '1.25rem'}} variant="p" component="p">
                  Current amount: <span style={{color: '#98A2B3!important'}}>{formattedAmt}</span>
                </SoftTypography>
                <SoftBox sx={{marginTop: '2.5rem'}}>
                  <form ref={formRef}>
                    <SoftTypography sx={{textFillColor: "#475467", fontSize: '14.5px', fontWeight: '400', marginTop: '1rem', marginBottom: '0.5rem'}} variant="p" component="p">
                      New score value
                    </SoftTypography>
                    <SoftInput
                      name="newScore"
                      type="number"
                      placeholder="Enter New value here"
                      onChange={(e) => {
                        setNewScore(e.target.value);
                      }}
                      sx={{
                        border: "1px solid #E4E7EC",
                        backgroundColor: "#F6F7F9 !important",
                        borderRadius: '4px',
                        color: "#1d2939 !important",
                        "& .MuiInputBase-input::placeholder": {
                          color: "#98A2B3 !important",
                          opacity: "1",
                        },
                        marginBottom: '1rem'
                      }}
                      size="large"
                    />
                    <SoftBox display="flex" justifyContent="space-between" alignItems="center" sx={{textAlign: 'center', marginTop: '2rem'}}>
                      <SoftButton sx={{width: '49%', marginTop: '1.5rem', background: 'rgba(15,23,42,0.03)!important', color: '#0F172A!important', border: '1px solid rgba(15,23,42,0.10)', fontWeight: '500', borderRadius: '4px'}} onClick={handleCloseModal}>
                        Cancel
                      </SoftButton>
                      <SoftButton
                        sx={{
                          width: '49%',
                          marginTop: '1.5rem',
                          borderRadius: '4px',
                          fontWeight: '500',
                          background: 'var(--gmb-primary, #1B598E)',
                          color: '#FFFFFF !important',
                          border: 0,
                          "&:hover": { background: 'var(--gmb-primary, #1B598E)', filter: 'brightness(0.96)' },
                        }}
                        onClick={handleSubmit}
                      >
                        Submit
                      </SoftButton>
                    </SoftBox>
                  </form>
                </SoftBox>
              </SoftBox>
            </Fade>
          </Modal>
          {openConfirmationPopUp && (
            <SoftDialog
              open={openConfirmationPopUp}
              onClose={handleConfirmationClose}
              title={`Delete ${timeline.kpiName} Score?`}
              type="error"
              icon={<WarningAmberIcon fontSize="large" sx={{ color: "#981B14" }} />}
              description={
                <SoftBox display="flex" flexDirection="column" alignItems="center" mt="25px" mb="35px">
                  <SoftTypography
                    variant="p"
                    sx={{
                      fontSize: "14px",
                      fontWeight: 500,
                      color: "#475467",
                      textAlign: "center",
                    }}
                  >
                    Are you sure you want to delete {timeline.name}'s {timeline.kpiName} KPI record?
                  </SoftTypography>
                </SoftBox>
              }
              footer={
                <SoftBox display="flex" gap="10px" justifyContent="center">
                  <SoftButton
                    size="small"
                    variant="contained"
                    color="outlined"
                    onClick={handleConfirmationClose}
                    sx={{
                      textTransform: "none",
                      borderRadius: "4px",
                      border: "1px solid rgba(233, 236, 239, 0.20)",
                      boxShadow: "0px 1px 2px 0px rgba(0, 0, 0, 0.05)",
                      background: "rgba(255, 255, 255, 0.10)",
                      color: "#CED4DA",
                      "&:hover": {
                        background: "rgba(255, 255, 255, 0.10)",
                      },
                    }}
                  >
                    Cancel
                  </SoftButton>
                  <SoftButton
                    size="small"
                    color="white"
                    onClick={() => {
                      handleKpiDelete(timeline._id);
                      handleConfirmationClose();
                    }}
                    sx={{
                      width: "min-content",
                      borderRadius: "4px",
                      textTransform: "none",
                      color: "#344054",
                    }}
                  >
                    Proceed
                  </SoftButton>
                </SoftBox>
              }
            />
          )}
          {/* <SoftBox mt={0.5}>
            <SoftTypography
              variant="caption"
              fontWeight="medium"
              color={isDark ? "secondary" : "text"}
            >
              {dateTime}
            </SoftTypography>
            </SoftBox> */}
          {isIntegratedLayout && displaySubtitle1 ? (
            <SoftBox mt={0.5}>
              <SoftTypography
                variant="button"
                fontWeight="regular"
                sx={{ color: "rgba(108, 117, 125, 1)", fontSize: "14px" }}
              >
                {displaySubtitle1}
              </SoftTypography>
            </SoftBox>
          ) : null}
          {isIntegratedLayout && (displayTechnician || resolvedJobTag || displayRefBadge) ? (
            <SoftBox mt={0.5} display="flex" alignItems="center" gap={1} flexWrap="wrap">
              {displayTechnician ? (
                <SoftTypography
                  variant="button"
                  fontWeight="medium"
                  sx={{ color: "rgba(52, 58, 64, 1)", fontSize: "14px" }}
                >
                  {displayTechnician}
                </SoftTypography>
              ) : null}
              {resolvedJobTag ? (
                <SoftBox
                  sx={{
                    borderRadius: "6px",
                    width: "max-content",
                    padding: "4px 8px",
                    backgroundColor: getHcpTagColor(resolvedJobTag),
                  }}
                >
                  <SoftTypography
                    sx={{
                      color: "primary.contrastText",
                      textTransform: "capitalize",
                      fontSize: "12px",
                    }}
                  >
                    {resolvedJobTag}
                  </SoftTypography>
                </SoftBox>
              ) : null}
              {displayRefBadge ? (
                <SoftBox
                  sx={{
                    borderRadius: "6px",
                    width: "max-content",
                    padding: "4px 8px",
                    // Clean Start reads teal so it separates from Clean 365 (amber).
                    backgroundColor:
                      String(displayRefBadge).trim().toLowerCase() === "clean start"
                        ? "#2DD4BF"
                        : "#FFB300",
                  }}
                >
                  <SoftTypography
                    sx={{
                      color: "#1A1A1A",
                      fontSize: "12px",
                      fontWeight: 600,
                    }}
                  >
                    {displayRefBadge}
                  </SoftTypography>
                </SoftBox>
              ) : null}
            </SoftBox>
          ) : null}
          {formFields.length > 0 ? (
            <SoftBox mt={0.5}>
              {formFields.map((field) => (
                <SoftTypography
                  key={field.label ? `${field.label}-${field.value}` : field.value}
                  variant="button"
                  fontWeight="regular"
                  sx={{
                    color: "rgba(108, 117, 125, 1)",
                    fontSize: "14px",
                    display: "block",
                  }}
                >
                  {field.label ? `${field.label} - ${field.value}` : field.value}
                </SoftTypography>
              ))}
            </SoftBox>
          ) : null}
          <SoftBox mt={0.5}>
            {renderDescription()}
          </SoftBox>
          {!isDeletedEntry && !hideAddedBy && timeline.reporterName ? (
            <SoftBox mt={0.5}>
              <SoftTypography
                variant="caption"
                sx={{
                  color: "rgba(108, 117, 125, 1)",
                  fontStyle: "italic",
                  display: "block",
                }}
              >
                Added by {timeline.reporterName} ·{" "}
                {formatCompanyDateTime(timeline.createdAt)}
              </SoftTypography>
            </SoftBox>
          ) : null}
          <SoftBox mt={0.5} mb={1}>
            {refItem}
          </SoftBox>
          {/* Same Dialog-based shell as the "View Review" modal (ResponsiveModal)
              — MUI Dialog centers itself on every viewport and caps its own
              height with internal scroll, instead of the old Backdrop + plain
              SoftBox below, which just top-anchored the card and let it run
              off both edges of the screen on a phone. FeedItem supplies its
              own card chrome, so this only adds the rounded Paper + a close
              button (view Review has no title bar to reuse here either). */}
          {referenceItem?.job && (
            <Dialog
              open
              onClose={() => setReferenceItem(null)}
              scroll="paper"
              fullWidth
              maxWidth="sm"
              PaperProps={{
                sx: {
                  borderRadius: "16px",
                  backgroundColor: "#fff",
                  border: "1px solid #EAECF0",
                  boxShadow: "0px 8px 8px rgba(16, 24, 40, 0.04), 0px 20px 24px rgba(16, 24, 40, 0.1)",
                  maxHeight: "calc(100vh - 32px)",
                  overflow: "hidden",
                },
              }}
              sx={{
                "& .MuiBackdrop-root": { background: "rgba(16, 24, 40, 0.4)" },
                "& .MuiPaper-root": { margin: { mini: "12px", sm: "24px" } },
              }}
            >
              <SoftBox sx={{ position: "relative", overflowY: "auto" }}>
                <IconButton
                  onClick={() => setReferenceItem(null)}
                  size="small"
                  aria-label="Close"
                  sx={{
                    position: "absolute",
                    top: "8px",
                    right: "8px",
                    zIndex: 5,
                    color: "#667085",
                    border: "1px solid #D0D5DD",
                    backgroundColor: "#fff",
                    "&:hover": { backgroundColor: "#fff" },
                  }}
                >
                  <CloseRoundedIcon fontSize="small" />
                </IconButton>
                <ThemeProvider theme={jobModalTheme}>
                  <FeedItem job={referenceItem.job} isModal />
                </ThemeProvider>
              </SoftBox>
            </Dialog>
          )}
          {referenceItem?.jobMissing && (
            <Dialog
              open
              onClose={() => setReferenceItem(null)}
              fullWidth
              maxWidth="xs"
              PaperProps={{
                sx: {
                  borderRadius: "16px",
                  backgroundColor: "#fff",
                  border: "1px solid #EAECF0",
                  boxShadow: "0px 8px 8px rgba(16, 24, 40, 0.04), 0px 20px 24px rgba(16, 24, 40, 0.1)",
                  p: 3,
                },
              }}
              sx={{
                "& .MuiBackdrop-root": { background: "rgba(16, 24, 40, 0.4)" },
              }}
            >
              <SoftBox display="flex" flexDirection="column" gap={1.5}>
                <SoftTypography variant="h6" fontWeight="bold">
                  Job not found
                </SoftTypography>
                <SoftTypography variant="body2" color="text">
                  This timeline entry references a job that isn&apos;t available here.
                  Timeline details above still apply.
                </SoftTypography>
                <SoftBox display="flex" justifyContent="flex-end" mt={1}>
                  <SoftButton
                    variant="gradient"
                    color="primary"
                    onClick={() => setReferenceItem(null)}
                  >
                    Close
                  </SoftButton>
                </SoftBox>
              </SoftBox>
            </Dialog>
          )}
          {/* Same Dialog shell as View Job/View Review/View Details above —
              KpiReferenceImage keeps its own rotate/save control (anchored to
              the image itself), this only adds the centered rounded Paper
              and a dialog-level close button. */}
          {referenceItem?.imageKey && (
            <Dialog
              open
              onClose={() => setReferenceItem(null)}
              scroll="paper"
              fullWidth
              maxWidth="sm"
              PaperProps={{
                sx: {
                  borderRadius: "16px",
                  backgroundColor: "#fff",
                  border: "1px solid #EAECF0",
                  boxShadow: "0px 8px 8px rgba(16, 24, 40, 0.04), 0px 20px 24px rgba(16, 24, 40, 0.1)",
                  maxHeight: "calc(100vh - 32px)",
                  overflow: "hidden",
                },
              }}
              sx={{
                "& .MuiBackdrop-root": { background: "rgba(16, 24, 40, 0.4)" },
                "& .MuiPaper-root": { margin: { mini: "12px", sm: "24px" } },
              }}
            >
              <SoftBox sx={{ position: "relative", overflowY: "auto", padding: { mini: 2, sm: 3 } }}>
                <IconButton
                  onClick={() => setReferenceItem(null)}
                  size="small"
                  aria-label="Close"
                  sx={{
                    position: "absolute",
                    top: "8px",
                    right: "8px",
                    zIndex: 5,
                    color: "#667085",
                    border: "1px solid #D0D5DD",
                    backgroundColor: "#fff",
                    "&:hover": { backgroundColor: "#fff" },
                  }}
                >
                  <CloseRoundedIcon fontSize="small" />
                </IconButton>
                <SoftBox sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
                  <KpiReferenceImage
                    src={GET_IMAGE(referenceItem.imageKey)}
                    entryId={referenceItem.entryId}
                    savedRotation={referenceItem.imageRotation ?? 0}
                    orientationSaved={referenceItem.orientationSaved}
                    onRotationSaved={(imageRotation) => {
                      if (referenceItem.entryId) {
                        savedImageRotationsRef.current[referenceItem.entryId] = imageRotation;
                      }
                      setReferenceItem((prev) =>
                        prev
                          ? { ...prev, imageRotation, orientationSaved: true }
                          : prev,
                      );
                    }}
                  />
                  <SoftTypography variant="body2">
                    {referenceItem.notes === "undefined" ? " " : referenceItem.notes}
                  </SoftTypography>
                </SoftBox>
              </SoftBox>
            </Dialog>
          )}
        </SoftBox>
      </SoftBox>
    </SoftBox>
  );
}

TimelineItem.defaultProps = {
  color: "info",
  lastItem: false,
  description: "",
  hideAddedBy: false,
};

// Typechecking props for the TimelineItem
TimelineItem.propTypes = {
  color: PropTypes.oneOf([
    "primary",
    "secondary",
    "info",
    "success",
    "warning",
    "error",
    "dark",
    "light",
  ]),
  icon: PropTypes.node.isRequired,
  title: PropTypes.string.isRequired,
  date: PropTypes.string.isRequired,
  description: PropTypes.string,
  hideAddedBy: PropTypes.bool,
  lastItem: PropTypes.bool,
};

export default TimelineItem;
